import { Button, Container, Typography } from "@mui/material";
import { captureSentryException } from "@utils";
import { Component, ErrorInfo } from "react";
import { BaseErrorBoundaryProps, ErrorBoundaryConfig } from "./types";

interface Props extends BaseErrorBoundaryProps, ErrorBoundaryConfig {
  // Generic context props that can be passed to Sentry
  context?: Record<string, any>;
  // Optional specialized state object for Sentry
  stateData?: Record<string, any>;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class GenericErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      `${this.props.errorBoundaryName} caught an error:`,
      error,
      errorInfo
    );

    // Capture error with Sentry including comprehensive context
    captureSentryException(error, {
      errorInfo,
      componentName: this.props.componentName,
      errorBoundary: this.props.errorBoundaryName,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      referrer: document.referrer,
      componentType: this.props.componentType,
      context: {
        ...this.props.context,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
      ...(this.props.stateData && {
        [this.props.componentType.toLowerCase() + "State"]:
          this.props.stateData,
      }),
    });

    this.props.onError?.(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container
          maxWidth="md"
          className="items-center justify-center flex flex-auto flex-col h-full min-h-[300px] p-4"
        >
          <Typography variant="h5" className="mb-4 text-center">
            Oops! Looks like something went wrong.
          </Typography>
          <Typography
            variant="body1"
            className="mb-4 text-center text-gray-600"
          >
            Please reload the page to continue. If the issue persists, please
            reach out to our team using the support chat or contact support@formly.ai.
          </Typography>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <Typography
              variant="body2"
              className="mb-4 text-center text-red-600 bg-red-50 p-2 rounded"
            >
              Error: {this.state.error.message}
            </Typography>
          )}
          <Button
            className="mt-4"
            variant="contained"
            onClick={this.handleReload}
          >
            Reload Page
          </Button>
        </Container>
      );
    }

    return this.props.children;
  }
}
